import {Icon} from '@iconify/react';
import classNames from 'classnames';
import React, {PropsWithChildren, useEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {useTranslation} from 'react-i18next';
import {useDisableBodyScroll} from '../../../utils/hooks/useDisableBodyScroll';
import {useKeyPress} from '../../../utils/hooks/useKeyPress';
import {useOnClickOutside} from '../../../utils/hooks/useOnClickOutside';

import './modal.scss';
import Button from '../../button/Button';

type ModalSize = 'lg' | 'md' | 'sm' | 'fit' | 'xl';

const CLOSE_ANIMATION_DURATION = 300; // 300 is animation duration set in modal.scss

export interface ModalProps {
    isOpen: boolean;
    header?: JSX.Element | string;
    showCancel?: boolean;
    cancelText?: string;
    description?: string;
    icon?: string;
    showConfirm?: boolean;
    confirmText?: string;
    onCancel: () => void;
    onConfirm?: () => void;
    formId?: string; // confirm button submits the form with this id
    confirmDisabled?: boolean;
    size?: ModalSize;
    noPadding?: boolean;
    dataCy?: string;
    closeOnClickOutside?: boolean;
    showOverflow?: boolean;
}

function ModalContent(props: PropsWithChildren<ModalProps>): JSX.Element {
    const {
        isOpen,
        header,
        showCancel = false,
        cancelText,
        description,
        icon,
        showConfirm = false,
        confirmText,
        onCancel,
        onConfirm,
        formId,
        confirmDisabled = false,
        children,
        size,
        noPadding,
        dataCy,
        closeOnClickOutside = true,
        showOverflow = false,
    } = props;

    const {t} = useTranslation();
    const bodyRef = useRef(null);
    const isEscPressed = useKeyPress('Escape');

    useEffect(() => {
        if (isEscPressed && isOpen) onCancel();
    }, [isEscPressed, isOpen, onCancel]);

    useOnClickOutside(bodyRef, () => {
        if (isOpen && closeOnClickOutside) onCancel();
    });

    useDisableBodyScroll(isOpen);

    return createPortal(
        <div
            className={classNames('c-modal is-open', {
                [`c-modal--${size}`]: size,
                'is-closing': !isOpen,
                'c-modal--showOverflow': showOverflow,
            })}
            aria-hidden={!isOpen}
            data-cy={dataCy}
        >
            <div className="c-modal__overlay" tabIndex={-1}>
                <div className="c-modal__body" role="dialog" aria-modal="true" ref={bodyRef}>
                    {/* HEADER */}
                    {header && (
                        <div className="c-modal__header">
                            <div className="c-modal__header_container">
                                {description && icon ? (
                                    <div className="c-modal__header_icon">
                                        <Icon icon={icon} />
                                    </div>
                                ) : null}
                                <div className={`${description && icon ? 'c-modal__header_content' : ''}`}>
                                    <h2 className="c-modal__header_title">{header}</h2>
                                    {description ? <p className="c-modal__header_description">{description}</p> : null}
                                </div>
                            </div>
                            <Button
                                className="c-modal__header_close"
                                variant="ghost"
                                size="md"
                                square
                                onClick={onCancel}
                                leading="ph:x-bold"
                                dataCy="close_modal_button"
                                type="button"
                            />
                        </div>
                    )}

                    {/* BODY */}
                    <div className={`c-modal__content c-modal__content--${noPadding ? 'noPadding' : ''}`}>{children}</div>

                    {/* FOOTER */}
                    {(showCancel || showConfirm) && (
                        <div className="c-modal__footer">
                            <div className="c-modal__footer_buttons">
                                {showCancel && (
                                    <Button type="button" color="primary" variant="outline" size="sm" onClick={onCancel}>
                                        {cancelText || t('common.cancel')}
                                    </Button>
                                )}
                                {showConfirm && (
                                    <Button
                                        data-cy="btnModalConfirm"
                                        disabled={confirmDisabled}
                                        color="secondary"
                                        variant="solid"
                                        size="sm"
                                        onClick={onConfirm}
                                        dataCy={`${dataCy}_confirm`}
                                        type={formId ? 'submit' : 'button'}
                                        form={formId}
                                    >
                                        {confirmText || t('common.confirm')}
                                    </Button>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
}

function Modal(props: PropsWithChildren<ModalProps>): JSX.Element | null {
    const {isOpen, onCancel} = props;
    const [isVisible, setIsVisible] = useState(isOpen);

    const lastOpenProps = useRef(props);
    if (isOpen) lastOpenProps.current = props;

    useEffect(() => {
        let timer: NodeJS.Timeout;
        if (isOpen) {
            setIsVisible(true);
        } else {
            timer = setTimeout(() => setIsVisible(false), CLOSE_ANIMATION_DURATION);
        }
        return () => clearTimeout(timer);
    }, [isOpen]);

    if (!isOpen && !isVisible) return null;

    return <ModalContent {...lastOpenProps.current} isOpen={isOpen} onCancel={onCancel} />;
}

export default Modal;
